/**
 * Client-side mirrors of the platform's field rules.
 *
 * 140: these are DUPLICATES of the server's rules, and that is a deliberate trade recorded in the
 * feature's data-model.md. The reason is `services/api.ts`: "Only the top-level reason is read; the
 * per-field `details` array is out of scope (contract C6)" — so `ApiError` carries no field
 * information, and per-field feedback can only come from validating here before submitting.
 *
 * 🔴 **The server is the enforcing side. This file is a convenience.** Every rule below is also
 * enforced at the boundary with a backend test that involves no browser, so if these drift the
 * experience degrades but the platform does not. Never move a rule *out* of the backend on the
 * strength of it appearing here.
 *
 * Within the app, though, one copy is enough: `ProfileEditPage` had its own `PHONE_RE` and
 * `RegisterPage` was about to grow a second. Two copies in one package is drift waiting to happen.
 */

/**
 * A phone number: optional leading `+`, then at least five characters of digits, spaces and dashes.
 * Mirrors `backend/src/shared/fieldSchemas.ts`'s `phoneField`. Accepts international numbers —
 * foreign visitors rent cars here.
 */
export const PHONE_RE = /^\+?[0-9][0-9\s-]{4,}$/;

/** The message the platform uses for a malformed number, matching the backend's wording. */
export const PHONE_INVALID_MESSAGE = 'Invalid phone number';

/**
 * The email shape, mirroring `backend/src/shared/fieldSchemas.ts`.
 *
 * Stricter than `type="email"`, which accepts `a@b` — no dot required in the domain. Relying on the
 * native check alone therefore lets through a request the platform is certain to refuse.
 */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EMAIL_MAX_LENGTH = 320;
export const NAME_MAX_LENGTH = 200;

/**
 * Validates the register form's non-password fields against the same rules the boundary applies, and
 * returns one message per offending field.
 *
 * Pure, so it is testable without rendering, and shared by nothing else yet — but the boundary rules
 * it mirrors live in one place on the server, which is what keeps the two comparable.
 *
 * 🔴 Whitespace-only values are the case native validation misses: `'   '` satisfies `required`, so
 * the browser is happy and the platform is not. That is why these rules earn their keep rather than
 * merely duplicating the browser.
 */
export function validateRegisterFields(input: {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password?: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};

  if (input.first_name.trim() === '') errors.first_name = 'First name is required';
  else if (input.first_name.trim().length > NAME_MAX_LENGTH)
    errors.first_name = 'First name is too long';

  if (input.last_name.trim() === '') errors.last_name = 'Last name is required';
  else if (input.last_name.trim().length > NAME_MAX_LENGTH)
    errors.last_name = 'Last name is too long';

  const email = input.email.trim();
  if (email === '') errors.email = 'Email address is required';
  else if (email.length > EMAIL_MAX_LENGTH || !EMAIL_RE.test(email))
    errors.email = 'Invalid email address';

  if (input.phone.trim() === '') errors.phone = 'Phone number is required';
  else if (!PHONE_RE.test(input.phone)) errors.phone = PHONE_INVALID_MESSAGE;

  // 140 US3: only checked when a password was supplied, because the field is `required` and the
  // browser stops an empty one before this runs.
  if (input.password !== undefined && !satisfiesPasswordPolicy(input.password)) {
    errors.password = PASSWORD_INVALID_MESSAGE;
  }

  return errors;
}

/**
 * The password policy, mirroring `backend/src/shared/passwordPolicy.ts`.
 *
 * 140: the rule is at least 8 characters with at least one uppercase letter and one digit. It has
 * existed on the server since 035 but governed only the change-password path, so a person setting a
 * password anywhere else met it for the first time as a rejection. Stating it up front is the point:
 * `minLength={8}` in the markup promised something narrower than the truth.
 */
export const PASSWORD_MIN_LENGTH = 8;

export const PASSWORD_RULE_TEXT =
  'At least 8 characters, with one uppercase letter and one number.';

/** Matches the backend predicate exactly. */
export function satisfiesPasswordPolicy(candidate: string): boolean {
  return (
    candidate.length >= PASSWORD_MIN_LENGTH && /[A-Z]/.test(candidate) && /[0-9]/.test(candidate)
  );
}

/**
 * The message shown when a password misses. Deliberately the same sentence as the rule itself: a
 * person who has just failed needs to be told what is wanted, not merely that they were wrong.
 */
export const PASSWORD_INVALID_MESSAGE = PASSWORD_RULE_TEXT;
